import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
} from "@/components/ui/sheet";
import { t } from "i18next";
import ConfirmDialog from "../ConfirmDialog";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import RecorderForm, {
  type RecorderFormHandle,
} from "../audio-recorder/recorder-form";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { useCreateNoteTemplate } from "@/hooks/notesTemplates/useCreateNoteTemplate";
import { useVisitStore } from "@/store/visit.store";
import { toast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";

export function NewNoteSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { createNoteTemplate, loading, error, result } =
    useCreateNoteTemplate();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const { file, getFile } = useVisitStore.getState();
  const { userData } = useAuthStore.getState();

  const handleFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (file?.transcription) {
      await createNoteTemplate({
        customTemplateId: formData.customTemplateId,
        fileId: file.id,
        exam: formData.examination,
        style: formData.style,
        transcription: file.transcription,
        template: formData.templateName,
        format: formData.format,
        macros: "",
      });
    }
  };

  useEffect(() => {
    if (!loading) {
      if (error) {
        toast({
          variant: "error",
          title: t("visit.messages.createFailed"),
        });
      }
      if (result && userData && file) {
        toast({
          variant: "success",
          title: t("visit.messages.createSuccessful"),
        });
        void getFile({ userId: userData.id, fileId: file.id }, true);
      }
    }
  }, [loading, error, getFile, userData, file, result]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!loading) {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent
        className="w-full sm:max-w-md p-0 sm:rounded-l-2xl"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <SheetTitle></SheetTitle>
        <SheetHeader />
        <div className="flex flex-col flex-1 h-full">
          <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
            <RecorderForm
              ref={recorderFormRef}
              onSubmitData={handleFormSubmit}
            />
          </div>
          <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
            <SubscriptionOnlyButton
              className="w-full"
              disabled={loading}
              size={"xl"}
              validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
              onClick={async () => {
                await recorderFormRef.current?.submitForm();
              }}
            >
              {loading
                ? `${t("visit.sidebar.newNote.procesing")}...`
                : t("visit.sidebar.newNote.submit")}
            </SubscriptionOnlyButton>
            <ConfirmDialog
              description={t("visits.sidebar.cancelConfirmation.description")}
              open={showCancelConfirmation}
              title={t("visits.sidebar.cancelConfirmation.title")}
              trigger={
                <Button
                  className="w-full"
                  disabled={loading}
                  size={"xl"}
                  variant={"grey"}
                >
                  {t("visits.sidebar.cancel")}
                </Button>
              }
              onOpenChange={setShowCancelConfirmation}
              onConfirm={() => {
                if (!loading) onOpenChange(false);
              }}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
